<script setup lang="ts">
import { computed } from 'vue'

interface Props {
  title?: string
  price?: string
  pageCount?: string | number
  publicationDate?: string
}

const props = withDefaults(defineProps<Props>(), {
  title: 'UNKNOWN COMIC',
  price: '35¢',
  pageCount: '?',
  publicationDate: '----',
})

const displayDate = computed(() => {
  if (!props.publicationDate) return ''
  return props.publicationDate.length > 15
    ? props.publicationDate.slice(0, 15) + '...'
    : props.publicationDate
})

const displayTitle = computed(() => {
  const maxLen = 22
  if (!props.title) return ''
  return props.title.length > maxLen
    ? props.title.slice(0, maxLen) + '...'
    : props.title
})

const burst = computed(() => {
  const cx = 350
  const cy = 540
  const outer = 300
  const inner = 205
  const pts = []

  for (let i = 0; i < 24; i++) {
    const angle = (Math.PI * 2 * i) / 24
    const r = i % 2 === 0 ? outer + 1.1 * i : inner - 1.1 * i
    pts.push(
      `${cx + Math.cos(angle) * r},${cy + Math.sin(angle) * r}`,
    )
  }

  return pts.join(' ')
})

interface Theme {
  banner: string
  price: string
  burst: string
  footer: string
  no_cover: string
  rotate: number
}

const themes: Array<Theme> = [
  { banner: '#f7d117', price: '#e63946', burst: '#4ea8ff', footer: '#111111', no_cover: 'Lost Issue', rotate: 0 }, // Original
  { banner: '#ffeb3b', price: '#d32f2f', burst: '#1976d2', footer: '#212121', no_cover: 'Cover Missing', rotate: 14 }, // Heroic Red/Blue/Yellow
  { banner: '#b2ff59', price: '#7b1fa2', burst: '#388e3c', footer: '#1a237e', no_cover: 'Hidden Gem', rotate: -1 }, // Green/Purple
  { banner: '#81d4fa', price: '#c62828', burst: '#f9a825', footer: '#06384F', no_cover: 'Cover Lost', rotate: 8 }, // Cyan/Red/Orange
  { banner: '#f48fb1', price: '#4a148c', burst: '#ad1457', footer: '#000000', no_cover: 'Missing Muse', rotate: 14 }, // Pink/Magenta
  { banner: '#ce93d8', price: '#311b92', burst: '#0097a7', footer: '#3A213F', no_cover: 'Purple Void', rotate: -8 }, // Purple/Teal
  { banner: '#ffcc80', price: '#bf360c', burst: '#2e7d32', footer: '#4e342e', no_cover: 'Lost Legend', rotate: -14 }, // Orange/Green/Brown
  { banner: '#e0e0e0', price: '#212121', burst: '#d84315', footer: '#000000', no_cover: 'Blank Panel', rotate: 8 }, // Grey/Dark/Red

  { banner: '#ef5350', price: '#fdd835', burst: '#1e88e5', footer: '#1b1b1b', no_cover: 'Hero Unknown', rotate: 14 }, // Superman
  { banner: '#ffd54f', price: '#212121', burst: '#e53935', footer: '#263238', no_cover: 'Golden Ghost', rotate: -14 }, // Golden Age
  { banner: '#80cbc4', price: '#6a1b9a', burst: '#ff7043', footer: '#004d40', no_cover: 'Alien Archive', rotate: 14 }, // Retro Sci-Fi
  { banner: '#c5e1a5', price: '#1565c0', burst: '#f4511e', footer: '#33691e', no_cover: 'Jungle Mystery', rotate: -14 }, // Jungle Adventure
  { banner: '#ffab91', price: '#ad1457', burst: '#3949ab', footer: '#3e2723', no_cover: 'Love Unknown', rotate: 14 }, // Romance Comic
  { banner: '#fff59d', price: '#5d4037', burst: '#8e24aa', footer: '#263238', no_cover: 'Secret Files', rotate: -8 }, // Mystery
  { banner: '#90caf9', price: '#ef6c00', burst: '#8bc34a', footer: '#102027', no_cover: 'Cosmic Gap', rotate: 8 }, // Cosmic Hero
  { banner: '#ff8a80', price: '#283593', burst: '#00acc1', footer: '#311b92', no_cover: 'Action Missing', rotate: -14 }, // Neon Action
  { banner: '#dcedc8', price: '#d81b60', burst: '#00c853', footer: '#1b4332', no_cover: 'Monster Found', rotate: 14 }, // Monster Comic
  { banner: '#ffe082', price: '#37474f', burst: '#e91e63', footer: '#212121', no_cover: 'Saturday Blank', rotate: -14 }, // Saturday Morning
  { banner: '#b39ddb', price: '#d84315', burst: '#26a69a', footer: '#2c1d4f', no_cover: 'Fantasy Lost', rotate: 14 }, // Fantasy Epic
  { banner: '#fff176', price: '#c2185b', burst: '#039be5', footer: '#263238', no_cover: 'Pop Mystery', rotate: -8 }, // Pop Art
  { banner: '#a5d6a7', price: '#880e4f', burst: '#ffca28', footer: '#1b4332', no_cover: 'Dark Encounter', rotate: -14 }, // Horror Pulp
]

function getHash(str: string): number {
  let h = 0
  for (let i = 0; i < str.length; i++) {
    h = ((h << 5) - h) + str.charCodeAt(i)
    h |= 0
  }
  return Math.abs(h)
}

const theme: ComputedRef<Theme> = computed(() => {
  const hash = getHash(props.title || '')
  return themes[hash % themes.length]!
})
</script>

<template>
  <svg
    viewBox="0 0 700 1050"
    xmlns="http://www.w3.org/2000/svg"
    role="img"
    :aria-label="title"
  >
    <defs>
      <pattern
        id="dots"
        x="0"
        y="0"
        width="16"
        height="16"
        patternUnits="userSpaceOnUse"
      >
        <circle
          cx="4"
          cy="4"
          r="2"
          fill="#d6d6d6"
        />
      </pattern>

      <filter id="shadow">
        <feDropShadow
          dx="0"
          dy="5"
          stdDeviation="6"
          flood-opacity=".25"
        />
      </filter>
    </defs>

    <!-- Cover -->
    <rect
      x="15"
      y="15"
      width="670"
      height="1020"
      rx="8"
      fill="#fefefe"
      stroke="#111"
      stroke-width="8"
      filter="url(#shadow)"
    />

    <!-- Background -->
    <rect
      x="30"
      y="180"
      width="640"
      height="760"
      fill="url(#dots)"
    />

    <!-- Top Banner -->
    <rect
      x="30"
      y="30"
      width="640"
      height="120"
      :fill="theme.banner"
      stroke="#111"
      stroke-width="5"
    />

    <text
      x="350"
      y="105"
      text-anchor="middle"
      font-size="48"
      font-weight="900"
      font-family="Luckiest Guy, Impact, sans-serif"
      fill="#111"
    >
      {{ displayTitle }}
    </text>

    <!-- Price -->
    <circle
      cx="600"
      cy="195"
      r="58"
      :fill="theme.price"
      stroke="#111"
      stroke-width="5"
    />

    <text
      x="600"
      y="213"
      text-anchor="middle"
      font-size="34"
      font-weight="bold"
      font-family="Luckiest Guy, Impact, sans-serif"
      fill="white"
    >
      {{ price }}
    </text>

    <!-- Date -->
    <rect
      x="45"
      y="175"
      width="190"
      height="60"
      fill="#fff"
      stroke="#111"
      stroke-width="4"
    />

    <text
      x="140"
      y="214"
      text-anchor="middle"
      font-size="24"
      font-weight="bold"
      font-family="Luckiest Guy, Impact, sans-serif"
    >
      {{ displayDate }}
    </text>

    <!-- Starburst -->
    <polygon
      :points="burst"
      :fill="theme.burst"
      stroke="#111"
      stroke-width="6"
      transform="rotate(15)"
      transform-origin="center"
    />

    <text
      x="350"
      y="559"
      text-anchor="middle"
      font-size="60"
      font-weight="900"
      font-family="Luckiest Guy, Impact, sans-serif"
      fill="white"
      :transform="`rotate(${theme.rotate})`"
      transform-origin="center"
    >
      {{ theme.no_cover }}
    </text>

    <!-- Footer -->
    <rect
      x="30"
      y="940"
      width="640"
      height="80"
      :fill="theme.footer"
    />

    <text
      x="350"
      y="993"
      text-anchor="middle"
      font-size="32"
      font-weight="bold"
      font-family="Luckiest Guy, Impact, sans-serif"
      fill="white"
    >
      {{ pageCount }} pages
    </text>
  </svg>
</template>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Luckiest+Guy&display=swap');
</style>
